import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { saveAs } from 'file-saver';
import {
  File as FileModel,
  ProposalRequest,
  ProposalRequestFile,
  ProposalRequestVendor
} from 'src/app/classes/models';
import { HyperlinkService } from 'src/app/services/hyperlink.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-quicksource-downloads',
  templateUrl: './quicksource-downloads.component.html',
  styleUrl: './quicksource-downloads.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class QuicksourceDownloadsComponent implements OnInit {
  constructor(
    private ToastService: ToastService,
    private route: ActivatedRoute,
    private UtilityService: UtilityService,
    private HyperlinkService: HyperlinkService,
    private cdr: ChangeDetectorRef
  ) {}

  hyperlinkHash!: string;

  ProposalRequest!: ProposalRequest;
  ProposalRequestFiles: ProposalRequestFile[] = [];
  ProposalRequestVendors: ProposalRequestVendor[] = [];

  proposalRequestFileSelected(File?: FileModel) {
    if (!File) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error downloading the bid request file.',
        { returnError: true }
      );
    }

    const { id: FileId } = File;

    if (!FileId) {
      throw this.ToastService.addErrorToast('Error', 'File is missing id.', {
        returnError: true
      });
    }

    this.HyperlinkService.getTemporaryHyperlinkProposalRequestFilePresignedUrl({
      ProposalRequestId: this.ProposalRequest.id!,
      FileId,
      hyperlinkHash: this.hyperlinkHash
    }).subscribe({
      next: ({ presignedUrl, filename }) => {
        saveAs(presignedUrl, filename);
      }
    });
  }

  proposalFileSelected(File?: FileModel) {
    if (!File) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error downloading the bid request file.',
        { returnError: true }
      );
    }

    const { id: FileId } = File;

    if (!FileId) {
      throw this.ToastService.addErrorToast('Error', 'File is missing id.', {
        returnError: true
      });
    }

    this.HyperlinkService.getTemporaryHyperlinkProposalFilePresignedUrl({
      ProposalRequestId: this.ProposalRequest.id!,
      FileId,
      hyperlinkHash: this.hyperlinkHash
    }).subscribe({
      next: ({ presignedUrl, filename }) => {
        saveAs(presignedUrl, filename);
      }
    });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe({
      next: (ParamMap) => {
        const hyperlinkHash = ParamMap.get('hyperlinkHash');

        if (!hyperlinkHash) {
          throw new Error('Missing hyperlink.');
        }

        this.hyperlinkHash = hyperlinkHash;

        this.HyperlinkService.getTemporaryHyperlink(
          this.UtilityService.stringifyObjectsInQueryObject({
            hyperlinkHash,
            ProposalRequest: {
              ProposalRequestFile: {
                options: { required: false },
                File: {
                  options: { required: false }
                }
              },
              ProposalRequestVendor: {
                attributes: ['id', 'legalName'],
                options: { required: false },
                Proposal: {
                  options: { required: false },
                  ProposalFile: {
                    options: { required: false },
                    File: {
                      options: { required: false }
                    }
                  }
                }
              }
            }
          })
        ).subscribe({
          next: (TemporaryHyperlink) => {
            if (!TemporaryHyperlink) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing hyperlink information.',
                { returnError: true }
              );
            }

            const { ProposalRequest } = TemporaryHyperlink;

            if (!ProposalRequest) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing bid request information.',
                { returnError: true }
              );
            }

            Object.assign(this, { ProposalRequest });

            const { ProposalRequestFiles = [], ProposalRequestVendors = [] } =
              ProposalRequest;

            this.ProposalRequestFiles.push(...ProposalRequestFiles);
            this.ProposalRequestVendors.push(...ProposalRequestVendors);

            // Zoneless: nothing re-renders the page after this async load
            // unless it is marked for check (VPDW-5273).
            this.cdr.markForCheck();
          }
        });
      }
    });
  }
}
